import { useState, useCallback, useEffect, useRef } from 'react';
import { Icon } from '../../icons';
import VideoGrid from './VideoGrid';
import LiveCamCell from './LiveCamCell';
import MatrixLayerSection from './MatrixLayerSection';
import { type VideoRendererProps, type StreamSource } from './VideoRenderer';
import { isPullSource, videoSourceBody } from './streamTypes';
import { channelFor, PRIMARY, type ChannelState, type FeedSource } from './aiChannels';
import { feedPrivacy, type OverlayState, type PrivacyMode } from './aiLayers';
import './MatrixView.css';

interface LiveCamera {
  id: string;
  name: string;
  lat: number;
  lon: number;
  source: string;
  provider: string;
  video_id: string;
}

interface MatrixViewProps extends Omit<VideoRendererProps, 'activeSourceId' | 'detectionEnabled'> {
  streamSources: StreamSource[];
  liveCameras: LiveCamera[];
  detectionEnabled: boolean;
  onClose: () => void;
  /** The main video panel's source — never released when a matrix tile closes. */
  mainPanelSourceId?: string;
  /**
   * Sync the detection flag into App state without re-posting to the backend.
   * Assigning a model already enables detection server-side.
   */
  onSetDetection?: (enabled: boolean) => void;
  /** App's GET /api/drone/overlays reading, for each tile's privacy chip. */
  overlayState?: OverlayState | null;
}

type SourceGroup = 'drone' | 'phone' | 'webcam' | 'other';

interface UnifiedSource {
  id: string;
  name: string;
  group: SourceGroup;
  isLiveCam: boolean;
  liveCam?: LiveCamera;
}

function groupLabel(g: SourceGroup): string {
  switch (g) {
    case 'drone': return 'Drone Feeds';
    case 'phone': return 'Phone Cameras';
    case 'webcam': return 'Live Webcams';
    case 'other': return 'Other Feeds';
  }
}

function classifySource(s: StreamSource): SourceGroup {
  if (s.type === 'dji' || s.type === 'hls' || s.type === 'demo') return 'drone';
  if (s.type === 'phone') return 'phone';
  if (isPullSource(s.type)) return 'other';
  return 'other';
}

export default function MatrixView({
  streamSources,
  liveCameras,
  detectionEnabled,
  onClose,
  mainPanelSourceId,
  onSetDetection,
  overlayState,
  ...rendererProps
}: MatrixViewProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [primaryId, setPrimaryId] = useState<string>('');
  const [channelState, setChannelState] = useState<ChannelState | null>(null);

  const allSources: UnifiedSource[] = [
    ...streamSources.map(s => ({
      id: s.id,
      name: s.name,
      group: classifySource(s) as SourceGroup,
      isLiveCam: false,
    })),
    ...liveCameras.map(c => ({
      id: `livecam-${c.id}`,
      name: c.name,
      group: 'webcam' as SourceGroup,
      isLiveCam: true,
      liveCam: c,
    })),
  ];

  const grouped: Record<SourceGroup, UnifiedSource[]> = { drone: [], phone: [], webcam: [], other: [] };
  allSources.forEach(s => grouped[s.group].push(s));

  const hasDjiSelected = selectedIds.some(id => {
    const s = streamSources.find(ss => ss.id === id);
    return s?.type === 'dji';
  });

  const { apiUrl, authHeaders } = rendererProps;

  /**
   * Tell the backend to drop the ffmpeg reader behind a closed tile. Only
   * server-read sources (rtmp/demo) have one — phone/HLS/webcam cells tear
   * themselves down when React unmounts them.
   *
   * `only_if_idle` makes the backend wait for the last viewer to disconnect
   * rather than killing the reader immediately, so we never cut off a stream
   * that is still on screen somewhere. We additionally skip the request when we
   * can already see another consumer: the main video panel, or another still-
   * selected tile pointing at the same URL.
   */
  const releaseFeed = useCallback((id: string, remaining: string[]) => {
    const src = streamSources.find(s => s.id === id);
    if (!src || (!isPullSource(src.type) && src.type !== 'demo')) return;
    if (id === mainPanelSourceId) return;

    const sharedWithOpenTile = remaining.some(rid => {
      const other = streamSources.find(s => s.id === rid);
      return !!other && other.type === src.type && (other.url || '') === (src.url || '');
    });
    if (sharedWithOpenTile) return;

    const body = videoSourceBody(src.type, src.url || '', { only_if_idle: true });

    fetch(`${apiUrl}/api/drone/video-source/stop`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify(body),
    }).catch(() => { /* teardown is best-effort; the idle reaper is the backstop */ });
  }, [streamSources, mainPanelSourceId, apiUrl, authHeaders]);

  const toggleSource = useCallback((id: string) => {
    if (selectedIds.includes(id)) {
      const next = selectedIds.filter(x => x !== id);
      setSelectedIds(next);
      if (primaryId === id) setPrimaryId(next.length > 0 ? next[0] : '');
      releaseFeed(id, next);
      return;
    }
    if (selectedIds.length >= 9) return;
    const next = [...selectedIds, id];
    setSelectedIds(next);
    if (!primaryId || !next.includes(primaryId)) setPrimaryId(id);
  }, [selectedIds, primaryId, releaseFeed]);

  const selectAll = useCallback(() => {
    const ids = allSources.map(s => s.id).slice(0, 9);
    setSelectedIds(ids);
    if (ids.length > 0 && !ids.includes(primaryId)) setPrimaryId(ids[0]);
  }, [allSources, primaryId]);

  const clearAll = useCallback(() => {
    selectedIds.forEach(id => releaseFeed(id, []));
    setSelectedIds([]);
    setPrimaryId('');
  }, [selectedIds, releaseFeed]);

  const removeFromGrid = useCallback((id: string) => {
    const next = selectedIds.filter(x => x !== id);
    setSelectedIds(next);
    if (primaryId === id) setPrimaryId(next.length > 0 ? next[0] : '');
    releaseFeed(id, next);
  }, [selectedIds, primaryId, releaseFeed]);

  // Closing the matrix entirely releases every feed it opened.
  const selectedRef = useRef<string[]>([]);
  const releaseRef = useRef(releaseFeed);
  selectedRef.current = selectedIds;
  releaseRef.current = releaseFeed;
  useEffect(() => () => {
    selectedRef.current.forEach(id => releaseRef.current(id, []));
  }, []);

  const isSourceDisabled = useCallback((source: UnifiedSource): boolean => {
    if (selectedIds.includes(source.id)) return false;
    if (selectedIds.length >= 9) return true;
    if (!source.isLiveCam) {
      const ss = streamSources.find(s => s.id === source.id);
      if (ss?.type === 'dji' && hasDjiSelected) return true;
    }
    return false;
  }, [selectedIds, streamSources, hasDjiSelected]);

  /**
   * Mirror the PRIMARY channel's model into App state.
   *
   * Only the primary: assigning a model to a secondary feed must not flip the
   * main video panel's DETECT badge, because nothing changed on the feed that
   * panel is showing. This posts nothing — the assign call already enabled
   * detection server-side, and re-posting here would fight it.
   */
  const handleChannelsChange = useCallback((next: ChannelState | null) => {
    setChannelState(next);
    if (!next || !onSetDetection) return;
    const primary = next.channels.find(c => c.name === PRIMARY);
    onSetDetection(!!primary?.model_slug);
  }, [onSetDetection]);

  /**
   * One feed in the shape the AI-layer rows and lookups want.
   *
   * `deviceSn` is what makes an aircraft assignable at all: a DJI tile's `url`
   * is the `.m3u8` it plays, which matches no stream key, while the backend has
   * been reading that aircraft's RTMP the whole flight. Naming the aircraft
   * targets that existing reader. Built here rather than in two places because
   * the picker and the model badge must agree about which feed a row is.
   */
  const toFeedSource = useCallback((s: StreamSource): FeedSource => ({
    id: s.id, name: s.name, type: s.type, url: s.url,
    deviceSn: rendererProps.hlsCredentialsMap?.[s.id]?.deviceSn,
  }), [rendererProps.hlsCredentialsMap]);

  /** The selected feeds, in the shape the AI-layer rows and lookups want. */
  const selectedFeedSources: FeedSource[] = selectedIds
    .map(id => streamSources.find(s => s.id === id))
    .filter((s): s is StreamSource => !!s)
    .map(toFeedSource);

  /**
   * The model running on one cell's feed, or null.
   *
   * Every cell is asked, not just the primary one: that is the whole point of
   * per-feed channels. The backend composites each feed separately, so a tile
   * showing a model name is a tile whose picture really carries that overlay.
   */
  const modelNameFor = useCallback((sourceId: string): string | null => {
    const src = streamSources.find(s => s.id === sourceId);
    if (!src || !channelState) return null;
    const channel = channelFor(toFeedSource(src), channelState.channels);
    if (!channel) return null;
    if (channel.model_slug) return channel.model_slug;
    return channel.road_seg ? 'road segmentation' : null;
  }, [streamSources, channelState, toFeedSource]);

  const privacyFor = useCallback((sourceId: string): PrivacyMode => {
    const src = streamSources.find(s => s.id === sourceId);
    return src ? feedPrivacy(overlayState ?? null, toFeedSource(src)) : 'picture';
  }, [streamSources, overlayState, toFeedSource]);

  const liveCamMap = new Map(liveCameras.map(c => [`livecam-${c.id}`, c]));

  const renderCell = useCallback((sourceId: string, isPrimary: boolean) => {
    const cam = liveCamMap.get(sourceId);
    if (!cam) return null;
    return (
      <LiveCamCell
        cam={cam}
        apiUrl={rendererProps.apiUrl}
        authHeaders={rendererProps.authHeaders}
      />
    );
  }, [liveCamMap, rendererProps.apiUrl, rendererProps.authHeaders]);

  const getCellLabel = useCallback((sourceId: string): string => {
    const unified = allSources.find(s => s.id === sourceId);
    return unified?.name || sourceId;
  }, [allSources]);

  return (
    <div className="matrix-overlay" onClick={onClose}>
      <div className="matrix-container" onClick={(e) => e.stopPropagation()}>
        <div className="matrix-header">
          <h3>MATRIX VIEW</h3>
          <div className="matrix-header-actions">
            <span className="matrix-count">{selectedIds.length} source{selectedIds.length !== 1 ? 's' : ''}</span>
            <button className="matrix-close-btn" onClick={onClose} aria-label="Close"><Icon name="clear" /></button>
          </div>
        </div>
        <div className="matrix-body">
          <div className="matrix-sidebar">
            <div className="matrix-sidebar-header">
              <span>Sources</span>
              <div className="matrix-sidebar-actions">
                <button className="matrix-sidebar-btn" onClick={selectAll}>All</button>
                <button className="matrix-sidebar-btn" onClick={clearAll}>Clear</button>
              </div>
            </div>
            <div className="matrix-source-list">
              <MatrixLayerSection
                apiUrl={rendererProps.apiUrl}
                authHeaders={rendererProps.authHeaders}
                sources={selectedFeedSources}
                onChannelsChange={handleChannelsChange}
              />
              {(['drone', 'phone', 'webcam', 'other'] as SourceGroup[]).map(group => {
                const sources = grouped[group];
                if (sources.length === 0) return null;
                return (
                  <div key={group} className="matrix-source-group">
                    <div className="matrix-group-label">{groupLabel(group)}</div>
                    {sources.map(source => {
                      const checked = selectedIds.includes(source.id);
                      const disabled = isSourceDisabled(source);
                      return (
                        <label
                          key={source.id}
                          className={`matrix-source-item ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggleSource(source.id)}
                          />
                          <span className={`matrix-source-dot ${source.group}`} />
                          <span className="matrix-source-name">{source.name}</span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="matrix-grid-area">
            {selectedIds.length === 0 ? (
              <div className="matrix-empty">
                <span>Select sources from the sidebar to view them here</span>
              </div>
            ) : (
              <VideoGrid
                gridSourceIds={selectedIds}
                primarySourceId={primaryId}
                streamSources={streamSources}
                onSetPrimary={setPrimaryId}
                onRemoveFromGrid={removeFromGrid}
                detectionEnabled={detectionEnabled}
                activeSourceId={primaryId}
                renderCell={renderCell}
                getCellLabel={getCellLabel}
                modelNameFor={modelNameFor}
                privacyFor={privacyFor}
                {...rendererProps}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
